/**
 * The Art Neon pricing engine.
 *
 * Reference prices are customer-facing EUR amounts. The curve is calibrated
 * to the approved 50 / 70 / 90 / 120 cm size list, protects the EUR 150
 * minimum below 50 cm and extends continuously to the 240 cm online limit.
 * The production base keeps the existing protected size price and dense-copy
 * character floor. A separate visible-character progression then guarantees
 * that every non-whitespace grapheme changes the customer price, even when the
 * protected size price is already higher than the character floor. Characters
 * use a graduated marginal rate: characters 1–6 cost EUR 2 each, character 7
 * costs EUR 4, character 8 costs EUR 6, character 9 costs EUR 8 and every
 * character from 10 onward costs EUR 10. Double-line fonts multiply only this
 * character component by 1.30. The progression is a top-up, never a
 * replacement, so the protected size and dense-copy pricing rules remain
 * valid lower bounds. Font complexity affects only the font-sensitive share
 * of the size price; the material/setup share is never multiplied a second
 * time. A minimum-width progression envelope fills only flat price steps:
 * every real 1 cm width increase raises the production base by at least EUR 1
 * until the normal size curve takes over. A separate per-font commercial
 * multiplier is applied only after those audited production rules, so a
 * requested 5% or 10% adjustment is reflected in the final customer price
 * instead of being diluted by the protected-size formula.
 */
((globalScope) => {
  const MINIMUM_PRICE_EUR = 150;
  const MAXIMUM_ONLINE_DIMENSION_CM = 240;
  const MINIMUM_FONT_FACTOR = 0.8;
  const MAXIMUM_FONT_FACTOR = 1.35;
  const MINIMUM_FONT_PRICE_MULTIPLIER = 0.5;
  const MAXIMUM_FONT_PRICE_MULTIPLIER = 2;
  const INCLUDED_CHARACTERS = 3;
  const CHARACTER_FLOOR_STEP_EUR = 10;
  const CHARACTER_RATE_FIRST_SIX_EUR = 2;
  const CHARACTER_RATE_SEVENTH_EUR = 4;
  const CHARACTER_RATE_EIGHTH_EUR = 6;
  const CHARACTER_RATE_NINTH_EUR = 8;
  const CHARACTER_RATE_FROM_TENTH_EUR = 10;
  const DOUBLE_LINE_CHARACTER_MULTIPLIER = 1.3;
  const PROTECTED_SIZE_SHARE = 0.625;
  const DISCOUNTABLE_SIZE_SHARE = 0.375;
  const MAXIMUM_CHARACTER_COUNT = 50;
  const MINIMUM_SIZE_STEP_EUR = 1;
  const ECONOMY_EFFICIENCY_START_CHARACTER = 10;
  const ECONOMY_EFFICIENCY_END_CHARACTER = 18;
  const ECONOMY_EFFICIENCY_CREDIT_EUR = 3;
  const LIGHT_EFFICIENCY_CREDIT_EUR = 0.25;

  /*
   * Each knot contains:
   * [maximum dimension cm, minimum price, setup component, area rate EUR/cm²]
   *
   * 50–120 cm values reproduce the commercial reference list after removing
   * its isolated 120 × 9 cm typo. 150–240 cm adds a gradual oversize,
   * packaging, power and damage-risk allowance without a price cliff.
   */
  const SIZE_PRICE_KNOTS = Object.freeze([
    Object.freeze([20, 150, 145, 0.05]),
    Object.freeze([30, 150, 143, 0.0525]),
    Object.freeze([40, 150, 145, 0.054]),
    Object.freeze([50, 177, 151, 0.0552]),
    Object.freeze([70, 212, 159, 0.05714285714285714]),
    Object.freeze([90, 263, 174, 0.05555555555555555]),
    Object.freeze([120, 296, 225, 0.035]),
    Object.freeze([150, 390, 285, 0.028]),
    Object.freeze([180, 480, 355, 0.023]),
    Object.freeze([210, 570, 430, 0.02]),
    Object.freeze([240, 660, 510, 0.018]),
  ]);

  const finiteNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const clamp = (value, minimum, maximum) =>
    Math.min(maximum, Math.max(minimum, finiteNumber(value, minimum)));

  const roundCurrency = (value) =>
    Math.round((finiteNumber(value) + Number.EPSILON) * 100) / 100;

  const interpolate = (start, end, ratio) => start + (end - start) * ratio;

  const priceParameters = (maximumDimension) => {
    const dimension = clamp(maximumDimension, SIZE_PRICE_KNOTS[0][0], MAXIMUM_ONLINE_DIMENSION_CM);
    const upperIndex = SIZE_PRICE_KNOTS.findIndex(([upper]) => dimension <= upper);
    if (upperIndex <= 0) {
      const knot = SIZE_PRICE_KNOTS[0];
      const [, minimum, setup, areaRate] = knot;
      return { dimension, minimum, setup, areaRate, lower: knot, upper: knot, ratio: 0 };
    }

    const lower = SIZE_PRICE_KNOTS[upperIndex - 1];
    const upper = SIZE_PRICE_KNOTS[upperIndex];
    const ratio = (dimension - lower[0]) / (upper[0] - lower[0]);

    return {
      dimension,
      minimum: interpolate(lower[1], upper[1], ratio),
      setup: interpolate(lower[2], upper[2], ratio),
      areaRate: interpolate(lower[3], upper[3], ratio),
      lower,
      upper,
      ratio,
    };
  };

  const priceAtKnot = (knot, minorDimension) => {
    const [dimension, configuredMinimum, setup, areaRate] = knot;
    const minimum = dimension < 50 ? MINIMUM_PRICE_EUR : configuredMinimum;
    const equivalentArea = dimension * Math.min(Math.max(0, minorDimension), dimension);
    return Math.max(minimum, setup + equivalentArea * areaRate);
  };

  const calculateSizePrice = (width, height) => {
    const normalizedWidth = Math.max(0, finiteNumber(width));
    const normalizedHeight = Math.max(0, finiteNumber(height));
    const maximumDimension = Math.max(normalizedWidth, normalizedHeight);
    const parameters = priceParameters(maximumDimension);
    const minimum =
      maximumDimension < 50 ? MINIMUM_PRICE_EUR : Math.max(MINIMUM_PRICE_EUR, parameters.minimum);
    const area = normalizedWidth * normalizedHeight;
    const minorDimension = Math.min(normalizedWidth, normalizedHeight);
    let runningMaximum = MINIMUM_PRICE_EUR;
    const monotoneKnotPrices = SIZE_PRICE_KNOTS.map((knot) => {
      runningMaximum = Math.max(runningMaximum, priceAtKnot(knot, minorDimension));
      return runningMaximum;
    });
    const lowerIndex = SIZE_PRICE_KNOTS.indexOf(parameters.lower);
    const upperIndex = SIZE_PRICE_KNOTS.indexOf(parameters.upper);
    const lowerPrice = monotoneKnotPrices[Math.max(0, lowerIndex)];
    const upperPrice = monotoneKnotPrices[Math.max(0, upperIndex)];
    const calculated = interpolate(lowerPrice, upperPrice, parameters.ratio);

    return {
      width: normalizedWidth,
      height: normalizedHeight,
      maximumDimension,
      area,
      minorDimension,
      minimum,
      setup: parameters.setup,
      areaRate: parameters.areaRate,
      raw: Math.max(minimum, calculated),
    };
  };

  const calculateFontFactor = (priceFactor, doubleLine, doubleLinePremium) => {
    const complexityFactor = clamp(priceFactor, MINIMUM_FONT_FACTOR, MAXIMUM_FONT_FACTOR);
    const linePremium = doubleLine
      ? clamp(doubleLinePremium, 0, MAXIMUM_FONT_FACTOR - MINIMUM_FONT_FACTOR)
      : 0;
    return {
      complexityFactor,
      linePremium,
      effectiveFactor: clamp(
        complexityFactor + linePremium,
        MINIMUM_FONT_FACTOR,
        MAXIMUM_FONT_FACTOR
      ),
    };
  };

  const calculateCharacterFloor = (characterCount, effectiveFontFactor) => {
    const normalizedCount = Math.floor(
      clamp(characterCount, 0, MAXIMUM_CHARACTER_COUNT)
    );
    const extraCharacters = Math.max(0, normalizedCount - INCLUDED_CHARACTERS);
    const premiumFactor = Math.max(
      1,
      clamp(effectiveFontFactor, MINIMUM_FONT_FACTOR, MAXIMUM_FONT_FACTOR)
    );
    /*
     * Economy and light fonts receive a bounded production-efficiency credit
     * on characters 10–18. The first nine visible characters keep their full
     * floor (Brooklyn remains EUR 210), while medium-length copy does not
     * become overpriced when the strict size-step envelope is applied.
     * Characters after 18 return to the full EUR 10 marginal step. The light
     * band receives only a small rounding calibration; the material economy
     * credit remains exclusive to the 0.80 band.
     */
    const efficiencyCharacters =
      effectiveFontFactor <= 0.9 + 0.000001
        ? Math.max(
            0,
            Math.min(
              normalizedCount,
              ECONOMY_EFFICIENCY_END_CHARACTER
            ) -
              ECONOMY_EFFICIENCY_START_CHARACTER +
              1
          )
        : 0;
    const efficiencyCreditPerCharacter =
      effectiveFontFactor <= MINIMUM_FONT_FACTOR + 0.000001
        ? ECONOMY_EFFICIENCY_CREDIT_EUR
        : effectiveFontFactor <= 0.9 + 0.000001
          ? LIGHT_EFFICIENCY_CREDIT_EUR
          : 0;
    const efficiencyCredit =
      efficiencyCharacters * efficiencyCreditPerCharacter;
    const rawBeforeCredit =
      MINIMUM_PRICE_EUR +
      extraCharacters * CHARACTER_FLOOR_STEP_EUR * premiumFactor;
    const raw = Math.max(
      MINIMUM_PRICE_EUR,
      rawBeforeCredit - efficiencyCredit
    );

    return {
      count: normalizedCount,
      includedCharacters: INCLUDED_CHARACTERS,
      extraCharacters,
      step: CHARACTER_FLOOR_STEP_EUR,
      premiumFactor,
      efficiencyCharacters,
      efficiencyCreditPerCharacter,
      efficiencyCredit,
      rawBeforeCredit,
      raw,
    };
  };

  /*
   * Every visible character has a production charge. The marginal single-line
   * rates are EUR 2 for characters 1–6, EUR 4 for character 7, EUR 6 for
   * character 8, EUR 8 for character 9 and EUR 10 from character 10 onward.
   * Double-line/outline fonts require more LED and labour, so only this
   * character component is multiplied by 1.30.
   *
   * This component is compared with, rather than blindly added on top of, the
   * legacy dense-copy floor. That avoids charging the same character twice
   * while still making every character affect large signs whose size price
   * previously masked the character floor.
   */
  const calculateCharacterCharge = (characterCount, doubleLine = false) => {
    const normalizedCount = Math.floor(
      clamp(characterCount, 0, MAXIMUM_CHARACTER_COUNT)
    );
    const firstSixCharacters = Math.min(normalizedCount, 6);
    const seventhCharacters = normalizedCount >= 7 ? 1 : 0;
    const eighthCharacters = normalizedCount >= 8 ? 1 : 0;
    const ninthCharacters = normalizedCount >= 9 ? 1 : 0;
    const fromTenthCharacters = Math.max(0, normalizedCount - 9);
    const lineMultiplier = doubleLine
      ? DOUBLE_LINE_CHARACTER_MULTIPLIER
      : 1;
    const firstSixChargeBeforeLineMultiplier =
      firstSixCharacters * CHARACTER_RATE_FIRST_SIX_EUR;
    const seventhChargeBeforeLineMultiplier =
      seventhCharacters * CHARACTER_RATE_SEVENTH_EUR;
    const eighthChargeBeforeLineMultiplier =
      eighthCharacters * CHARACTER_RATE_EIGHTH_EUR;
    const ninthChargeBeforeLineMultiplier =
      ninthCharacters * CHARACTER_RATE_NINTH_EUR;
    const fromTenthChargeBeforeLineMultiplier =
      fromTenthCharacters * CHARACTER_RATE_FROM_TENTH_EUR;
    const rawBeforeLineMultiplier =
      firstSixChargeBeforeLineMultiplier +
      seventhChargeBeforeLineMultiplier +
      eighthChargeBeforeLineMultiplier +
      ninthChargeBeforeLineMultiplier +
      fromTenthChargeBeforeLineMultiplier;
    const raw = roundCurrency(rawBeforeLineMultiplier * lineMultiplier);

    return {
      count: normalizedCount,
      firstSixCharacters,
      seventhCharacters,
      eighthCharacters,
      ninthCharacters,
      fromTenthCharacters,
      firstSixRate: CHARACTER_RATE_FIRST_SIX_EUR,
      seventhRate: CHARACTER_RATE_SEVENTH_EUR,
      eighthRate: CHARACTER_RATE_EIGHTH_EUR,
      ninthRate: CHARACTER_RATE_NINTH_EUR,
      fromTenthRate: CHARACTER_RATE_FROM_TENTH_EUR,
      doubleLine: Boolean(doubleLine),
      lineMultiplier,
      firstSixChargeBeforeLineMultiplier,
      seventhChargeBeforeLineMultiplier,
      eighthChargeBeforeLineMultiplier,
      ninthChargeBeforeLineMultiplier,
      fromTenthChargeBeforeLineMultiplier,
      rawBeforeLineMultiplier,
      firstSixEffectiveRate: roundCurrency(
        CHARACTER_RATE_FIRST_SIX_EUR * lineMultiplier
      ),
      seventhEffectiveRate: roundCurrency(
        CHARACTER_RATE_SEVENTH_EUR * lineMultiplier
      ),
      eighthEffectiveRate: roundCurrency(
        CHARACTER_RATE_EIGHTH_EUR * lineMultiplier
      ),
      ninthEffectiveRate: roundCurrency(
        CHARACTER_RATE_NINTH_EUR * lineMultiplier
      ),
      fromTenthEffectiveRate: roundCurrency(
        CHARACTER_RATE_FROM_TENTH_EUR * lineMultiplier
      ),
      raw,
    };
  };

  const calculateProtectedSizePrice = (sizePrice, effectiveFontFactor) => {
    const normalizedSizePrice = Math.max(
      MINIMUM_PRICE_EUR,
      finiteNumber(sizePrice, MINIMUM_PRICE_EUR)
    );
    const normalizedFactor = clamp(
      effectiveFontFactor,
      MINIMUM_FONT_FACTOR,
      MAXIMUM_FONT_FACTOR
    );
    /*
     * Size already captures the font's physical footprint. Applying a premium
     * factor to the whole size price double-charges tall/wide fonts and creates
     * excessive gaps between visually similar styles. The factor therefore
     * applies symmetrically only to the font-sensitive production share.
     */
    const appliedFactor =
      PROTECTED_SIZE_SHARE + DISCOUNTABLE_SIZE_SHARE * normalizedFactor;

    return {
      protectedShare: PROTECTED_SIZE_SHARE,
      discountableShare: DISCOUNTABLE_SIZE_SHARE,
      appliedFactor,
      raw: normalizedSizePrice * appliedFactor,
    };
  };

  const calculateNaturalProductionBase = (
    width,
    height,
    effectiveFontFactor,
    characterFloor
  ) => {
    const size = calculateSizePrice(width, height);
    const protectedSize = calculateProtectedSizePrice(
      size.raw,
      effectiveFontFactor
    );
    const raw = Math.max(
      MINIMUM_PRICE_EUR,
      protectedSize.raw,
      characterFloor.raw
    );
    const priceDriver =
      characterFloor.raw > protectedSize.raw
        ? 'character-floor'
        : protectedSize.raw > characterFloor.raw
          ? 'protected-size'
          : 'balanced-floor';

    return {
      size,
      protectedSize,
      raw,
      priceDriver,
    };
  };

  /*
   * The character floor is intentionally strong, but a hard max() can make a
   * larger sign display the same whole-euro price for many slider steps. Build
   * the least expensive strictly increasing envelope from this design's real
   * minimum width. It adds only what is needed to make each 1 cm step worth at
   * least `minimumStep`; once the natural size curve grows faster, the uplift
   * automatically returns to zero.
   */
  const calculateSizeProgression = ({
    width,
    height,
    minimumWidth,
    aspectRatio,
    minimumStep = MINIMUM_SIZE_STEP_EUR,
    effectiveFontFactor,
    characterFloor,
    currentNaturalBase,
  }) => {
    const currentWidth = Math.max(
      1,
      Math.round(clamp(width, 1, MAXIMUM_ONLINE_DIMENSION_CM))
    );
    const currentHeight = Math.max(1, Math.round(finiteNumber(height, 1)));
    const requestedMinimumWidth = Number.isFinite(Number(minimumWidth))
      ? Number(minimumWidth)
      : currentWidth;
    const startWidth = Math.min(
      currentWidth,
      Math.max(
        1,
        Math.round(
          clamp(
            requestedMinimumWidth,
            1,
            MAXIMUM_ONLINE_DIMENSION_CM
          )
        )
      )
    );
    const fallbackRatio = currentHeight / currentWidth;
    const normalizedAspectRatio = clamp(
      aspectRatio,
      0.01,
      10
    );
    const heightPerWidth = Number.isFinite(Number(aspectRatio))
      ? normalizedAspectRatio
      : fallbackRatio;
    const step = clamp(minimumStep, MINIMUM_SIZE_STEP_EUR, 10);

    let minimumBase = null;
    let monotonicBase = null;

    for (let stepWidth = startWidth; stepWidth <= currentWidth; stepWidth += 1) {
      const isCurrentStep = stepWidth === currentWidth;
      const stepHeight = isCurrentStep
        ? currentHeight
        : Math.max(1, Math.round(stepWidth * heightPerWidth));
      const naturalBase = isCurrentStep
        ? currentNaturalBase
        : calculateNaturalProductionBase(
            stepWidth,
            stepHeight,
            effectiveFontFactor,
            characterFloor
          );

      if (monotonicBase === null) {
        monotonicBase = naturalBase.raw;
        minimumBase = naturalBase.raw;
      } else {
        monotonicBase = Math.max(
          naturalBase.raw,
          monotonicBase + step
        );
      }
    }

    const naturalRaw = currentNaturalBase.raw;
    const raw = Math.max(naturalRaw, monotonicBase ?? naturalRaw);

    return {
      minimumWidth: startWidth,
      minimumHeight: Math.max(1, Math.round(startWidth * heightPerWidth)),
      currentWidth,
      currentHeight,
      aspectRatio: heightPerWidth,
      step,
      steps: Math.max(0, currentWidth - startWidth),
      minimumBase: minimumBase ?? naturalRaw,
      naturalBase: naturalRaw,
      raw,
      uplift: Math.max(0, raw - naturalRaw),
      active: raw > naturalRaw + 0.000001,
    };
  };

  const calculate = ({
    width,
    height,
    minimumWidth,
    aspectRatio,
    minimumSizeStep = MINIMUM_SIZE_STEP_EUR,
    priceFactor = 1,
    priceMultiplier = 1,
    doubleLine = false,
    doubleLinePremium = 0.1,
    characterCount = 0,
    optionPercent = 0,
    displayedDiscountPercent = 0,
  }) => {
    const font = calculateFontFactor(priceFactor, doubleLine, doubleLinePremium);
    const characterFloor = calculateCharacterFloor(
      characterCount,
      font.effectiveFactor
    );
    const characterCharge = calculateCharacterCharge(
      characterCount,
      doubleLine
    );
    const naturalBase = calculateNaturalProductionBase(
      width,
      height,
      font.effectiveFactor,
      characterFloor
    );
    const { size, protectedSize } = naturalBase;
    const fontAdjusted = Math.max(
      MINIMUM_PRICE_EUR,
      size.raw * font.effectiveFactor
    );
    const sizeProgression = calculateSizeProgression({
      width,
      height,
      minimumWidth,
      aspectRatio,
      minimumStep: minimumSizeStep,
      effectiveFontFactor: font.effectiveFactor,
      characterFloor,
      currentNaturalBase: naturalBase,
    });
    /*
     * Build the size-only baseline with the exact same size, font and minimum
     * width rules. The explicit character schedule is then measured from that
     * baseline. Taking the maximum preserves every existing character-floor
     * price while filling any flat character steps that size pricing masked.
     */
    const baselineCharacterFloor = calculateCharacterFloor(
      0,
      font.effectiveFactor
    );
    const baselineNaturalBase = calculateNaturalProductionBase(
      width,
      height,
      font.effectiveFactor,
      baselineCharacterFloor
    );
    const baselineSizeProgression = calculateSizeProgression({
      width,
      height,
      minimumWidth,
      aspectRatio,
      minimumStep: minimumSizeStep,
      effectiveFontFactor: font.effectiveFactor,
      characterFloor: baselineCharacterFloor,
      currentNaturalBase: baselineNaturalBase,
    });
    const characterProgressionTarget =
      baselineSizeProgression.raw + characterCharge.raw;
    const auditedProductionBase = Math.max(
      sizeProgression.raw,
      characterProgressionTarget
    );
    const normalizedFontPriceMultiplier = clamp(
      priceMultiplier,
      MINIMUM_FONT_PRICE_MULTIPLIER,
      MAXIMUM_FONT_PRICE_MULTIPLIER
    );
    const productionBase =
      auditedProductionBase * normalizedFontPriceMultiplier;
    const normalizedOptionPercent = clamp(optionPercent, 0, 500);
    const optionMultiplier = 1 + normalizedOptionPercent / 100;
    const net = productionBase * optionMultiplier;
    const existingTotal = Math.max(
      MINIMUM_PRICE_EUR,
      Math.round(net)
    );
    /*
     * The EUR 150 customer minimum used to hide character changes on small
     * signs with a discounted font multiplier. Anchor the explicit character
     * schedule to the actual size-only customer total, then add the scaled
     * cumulative character amount after that minimum. This leaves every
     * zero-character and legacy price untouched, while guaranteeing a visible
     * whole-euro increase from the very first character.
     */
    const customerBaselineNet =
      baselineSizeProgression.raw *
      normalizedFontPriceMultiplier *
      optionMultiplier;
    const customerBaseline = Math.max(
      MINIMUM_PRICE_EUR,
      Math.round(customerBaselineNet)
    );
    const scaledCharacterCharge =
      characterCharge.raw *
      normalizedFontPriceMultiplier *
      optionMultiplier;
    const roundedCharacterCharge =
      characterCharge.count > 0
        ? Math.max(1, Math.round(scaledCharacterCharge))
        : 0;
    const customerTarget =
      customerBaseline + roundedCharacterCharge;
    const total = Math.max(existingTotal, customerTarget);
    const characterProgression = {
      baseline: baselineSizeProgression.raw,
      existingPrice: sizeProgression.raw,
      target: characterProgressionTarget,
      productionUplift: Math.max(
        0,
        auditedProductionBase - sizeProgression.raw
      ),
      raw: auditedProductionBase,
      customerBaseline,
      scaledCharacterCharge,
      roundedCharacterCharge,
      existingTotal,
      customerTarget,
      uplift: Math.max(0, total - existingTotal),
      active: customerTarget > existingTotal,
    };
    const priceDriver = characterProgression.active
      ? 'character-progression'
      : sizeProgression.active
        ? 'size-progression'
        : naturalBase.priceDriver;

    /*
     * The supplied reference table is the protected customer price. The
     * section's discount setting controls the displayed compare-at amount;
     * it never silently reduces the approved net price.
     */
    const discountPercent = clamp(displayedDiscountPercent, 0, 50);
    const list =
      discountPercent > 0
        ? Math.max(total, Math.ceil(total / (1 - discountPercent / 100)))
        : total;

    return {
      version: 'tan-pricing-v8-graduated-character-ladder-2026-07',
      minimumPrice: MINIMUM_PRICE_EUR,
      maximumOnlineDimension: MAXIMUM_ONLINE_DIMENSION_CM,
      size,
      font,
      protectedSize,
      characterFloor,
      characterCharge,
      characterProgression,
      naturalProductionBase: naturalBase.raw,
      naturalPriceDriver: naturalBase.priceDriver,
      sizeProgression,
      fontPriceMultiplier: normalizedFontPriceMultiplier,
      productionBase,
      priceDriver,
      optionPercent: normalizedOptionPercent,
      discountPercent,
      fontAdjusted,
      list,
      total,
    };
  };

  const api = Object.freeze({
    version: 'tan-pricing-v8-graduated-character-ladder-2026-07',
    minimumPrice: MINIMUM_PRICE_EUR,
    maximumOnlineDimension: MAXIMUM_ONLINE_DIMENSION_CM,
    sizePriceKnots: SIZE_PRICE_KNOTS,
    calculateSizePrice,
    calculateFontFactor,
    calculateCharacterFloor,
    calculateCharacterCharge,
    calculateProtectedSizePrice,
    calculateNaturalProductionBase,
    calculateSizeProgression,
    calculate,
  });

  if (globalScope) globalScope.TanNeonPricing = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
